/**
 * Дебиторка и кредиторка на последнюю дату снимка.
 *
 * Стороны долга не складываются: дебиторская отбирается фильтром
 * `kind = Дебиторская`, кредиторская показывается отдельным блоком.
 * Период к долгам не применяется — это остаток на дату, а не оборот.
 *
 * Долги между своими организациями (контрагент с ИНН нашей организации)
 * по умолчанию в итог не входят — так решено до ответа клиента, и это
 * переключается в админке. Сумма, которая не вошла, всегда видна строкой.
 */

import { useState, type CSSProperties } from 'react'
import { useFinance } from '../context'
import { num, rowKey } from '../data'
import { SegmentBar } from '../charts'
import { Caveat, closedReason, DataNotes, Kpi, Panel, Segmented, SkeletonRows } from '../parts'
import { originFrom } from '../Drawer'
import { BUCKETS, useCreditors, useDebtBranches, useDebtRelations, useDebtorsBy } from '../queries'
import { RELATION_EXTERNAL, RELATION_OWN } from '../settings'
import { money, over60, overdueOf } from './Overview'
import { usePublishFreshness } from '../freshness'
import { formatDateTime, formatMoneyShort, formatNumber, formatPercent, pluralize, toNumber } from '../../format'

type Sort = 'amount' | 'overdue' | 'over60' | 'days'
type View = 'external' | 'own' | 'all'

const VIEW_RELATION: Record<View, string | null> = { external: RELATION_EXTERNAL, own: RELATION_OWN, all: null }

export function Debts() {
  const { branch, branchIgnored, branchName, catalog, drill, debtsExternalOnly } = useFinance()
  const relationKnown = catalog.hasDimension('receivables', 'relation')
  const [view, setView] = useState<View>(debtsExternalOnly ? 'external' : 'all')
  const relation = relationKnown ? VIEW_RELATION[view] : null

  const debtors = useDebtorsBy(relation)
  const creditors = useCreditors(relation)
  const relations = useDebtRelations()
  const branches = useDebtBranches(relation)
  const [sort, setSort] = useState<Sort>('amount')
  usePublishFreshness([debtors])

  const kindKnown = catalog.hasDimension('receivables', 'kind')
  const totals = debtors.data?.totals ?? {}
  const rows = debtors.data?.rows ?? []
  const amount = num(totals.amount)
  const overdue = overdueOf(totals)
  const over60Total = num(totals.overdue_61_90) + num(totals.overdue_90_plus)
  const over60Count = rows.filter((row) => over60(row) > 0).length
  const overdueCount = rows.filter((row) => overdueOf(row) > 0).length
  const creditorRows = creditors.data?.rows ?? []
  const payable = toNumber(creditors.data?.totals.amount)

  // Сколько долга между своими: строка «не включено» и подсказка у переключателя.
  const ownOf = (kind: string) =>
    (relations.data?.rows ?? []).filter((row) => row.kind === kind && row.relation === RELATION_OWN).reduce((sum, row) => sum + num(row.amount), 0)
  const ownReceivable = ownOf('Дебиторская')
  const ownPayable = ownOf('Кредиторская')
  const ownOverdue60 = (relations.data?.rows ?? [])
    .filter((row) => row.kind === 'Дебиторская' && row.relation === RELATION_OWN)
    .reduce((sum, row) => sum + num(row.overdue_61_90) + num(row.overdue_90_plus), 0)

  const sorted = [...rows].sort((a, b) => {
    if (sort === 'overdue') return overdueOf(b) - overdueOf(a)
    if (sort === 'over60') return over60(b) - over60(a)
    if (sort === 'days') return num(b.max_overdue_days) - num(a.max_overdue_days)
    return num(b.amount) - num(a.amount)
  })

  const bucketCount = (key: string) => rows.filter((row) => num(row[key]) > 0).length
  const asOf = debtors.data?.meta.data_as_of ?? null
  // «8 внешних контрагентов», а не «8 контрагентов · внешних контрагентов».
  const counterparties =
    view === 'external'
      ? pluralize(rows.length, 'внешний контрагент', 'внешних контрагента', 'внешних контрагентов')
      : `${pluralize(rows.length, 'контрагент', 'контрагента', 'контрагентов')} ${view === 'own' ? 'между своими' : 'всего'}`

  return (
    <div className="fin-stack">
      <Caveat>
        Долги — на последнюю дату снимка{asOf ? ` (данные 1С на ${formatDateTime(asOf)})` : ''}, выбранный
        период к ним не применяется.
        {branchIgnored('receivables') && ` Взаиморасчёты не привязаны к городу: показана вся компания, фильтр «${branchName ?? 'выбранный город'}» не действует.`}
        {branch && !branchIgnored('receivables') &&
          ` Город у долга — из документа-основания, а где его нет — по последней продаже контрагенту. Долг, город которого не определить, в «${branchName ?? 'выбранный город'}» не попадает.`}
      </Caveat>
      {!kindKnown && (
        <Caveat tone="warn">
          Отчёт пока не различает дебиторскую и кредиторскую задолженность — суммы ниже могут включать обе стороны.
        </Caveat>
      )}

      {relationKnown && (
        <div className="debt-view">
          <Segmented
            label="Какие долги показать"
            value={view}
            onChange={setView}
            options={[
              { value: 'external', label: 'Внешние' },
              { value: 'own', label: 'Между своими' },
              { value: 'all', label: 'Все' },
            ]}
          />
          {relations.status === 'ready' && (ownReceivable > 0 || ownPayable > 0) && (
            <span className="small muted">
              {view === 'external' ? (
                <>
                  Между своими: дебиторка {money(ownReceivable)}, кредиторка {money(ownPayable)} — <span className="strong">не включено</span>
                </>
              ) : view === 'all' ? (
                <>В т. ч. между своими: дебиторка {money(ownReceivable)}, кредиторка {money(ownPayable)}</>
              ) : (
                <>Долги своих организаций друг другу — контрагент с ИНН нашей организации</>
              )}
            </span>
          )}
        </div>
      )}
      {relationKnown && view === 'external' && ownOverdue60 > 0 && (
        <Caveat>
          Просрочка свыше 60 дней между своими — {money(ownOverdue60)} — в сигналы не попадает: это долги своих
          ИП и организаций друг другу. Включать ли их в общий итог, решает собственник; настройка — в админке
          «Финансовая разметка».
        </Caveat>
      )}

      <div className="kpi-grid">
        <Kpi index={0} label="Дебиторская задолженность" memoryKey="debt.amount" value={debtors.status === 'ready' ? amount : null} format={money} state={debtors.status} closedText={closedReason(debtors, 'Задолженность не входит в ваши права')} hint={counterparties} />
        <Kpi
          index={1}
          label="Просрочено"
          memoryKey="ov.overdue"
          value={debtors.status === 'ready' ? overdue : null}
          format={money}
          state={debtors.status} closedText={closedReason(debtors, 'Задолженность не входит в ваши права')}
          tone={overdue > 0 ? 'bad' : undefined}
          hint={amount > 0 ? `${formatPercent((overdue / amount) * 100)} долга · ${pluralize(overdueCount, 'должник', 'должника', 'должников')}` : undefined}
        />
        <Kpi
          index={2}
          label="Свыше 60 дней"
          memoryKey="debt.over60"
          value={debtors.status === 'ready' ? over60Total : null}
          format={money}
          state={debtors.status} closedText={closedReason(debtors, 'Задолженность не входит в ваши права')}
          tone={over60Total > 0 ? 'bad' : undefined}
          hint={pluralize(over60Count, 'контрагент', 'контрагента', 'контрагентов')}
        />
        <Kpi
          index={3}
          label="Кредиторская задолженность"
          memoryKey="debt.payable"
          value={kindKnown && creditors.status === 'ready' ? (payable ?? 0) : null}
          format={money}
          state={kindKnown ? creditors.status : 'idle'}
          closedText="Бэкенд пока не разделяет стороны долга"
          hint={kindKnown && creditors.status === 'ready' && creditorRows.length === 0 ? 'В данных 1С нет долгов перед поставщиками' : 'Наш долг поставщикам'}
        />
      </div>

      <Panel
        index={1}
        title="Дебиторка по срокам просрочки"
        subtitle="Сумма и число должников в каждой корзине"
        states={[debtors]}
        skeleton={<SkeletonRows rows={5} height={14} />}
        empty={{ when: amount === 0, title: 'Дебиторской задолженности нет', hint: 'На дату снимка никто не должен.' }}
      >
        {() => (
          <div className="stack" style={{ gap: 18 }}>
            <SegmentBar
              ariaLabel="Структура дебиторской задолженности по срокам"
              segments={BUCKETS.map((bucket) => ({ key: bucket.key, label: bucket.label, color: bucket.color, value: num(totals[bucket.key]) }))}
            />
            <div className="buckets">
              {BUCKETS.map((bucket, index) => {
                const value = num(totals[bucket.key])
                return (
                  <div key={bucket.key} className="bucket cascade" style={{ '--i': Math.min(index, 7) } as CSSProperties}>
                    <div className="row row--tight" style={{ flexWrap: 'nowrap' }}>
                      <span className="chart-legend__swatch" style={{ background: bucket.color }} aria-hidden="true" />
                      <span className="small secondary">{bucket.label}</span>
                    </div>
                    <div className="bucket__value mono">{money(value)}</div>
                    <div className="tiny muted">
                      {amount > 0 ? formatPercent((value / amount) * 100) : '—'} · {pluralize(bucketCount(bucket.key), 'должник', 'должника', 'должников')}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </Panel>

      {branches.status !== 'idle' && <DebtBranches state={branches} />}

      <Panel
        index={2}
        title="Крупнейшие должники"
        subtitle="Строка открывает разбивку долга контрагента"
        states={[debtors]}
        skeleton={<SkeletonRows rows={8} height={16} />}
        empty={{ when: rows.length === 0, title: 'Должников нет', hint: view === 'own' ? 'Долгов между своими организациями на дату снимка нет.' : undefined }}
        actions={
          <Segmented
            size="sm"
            label="Сортировка"
            value={sort}
            onChange={setSort}
            options={[
              { value: 'amount', label: 'Долг' },
              { value: 'overdue', label: 'Просрочка' },
              { value: 'over60', label: '60+' },
              { value: 'days', label: 'Дни' },
            ]}
          />
        }
      >
        {() => <DebtTable rows={sorted.slice(0, 15)} total={amount} onOpen={(row, element) => drill({ kind: 'debtor', value: rowKey(row, 'counterparty'), label: String(row.counterparty ?? '—') }, originFrom(element))} />}
      </Panel>

      {kindKnown && (
        <Panel
          index={3}
          title="Кредиторская задолженность"
          subtitle="Наши долги поставщикам — отдельно от дебиторской, не складываются"
          states={[creditors]}
          skeleton={<SkeletonRows rows={4} height={16} />}
          empty={{
            when: creditorRows.length === 0,
            title: 'Кредиторской задолженности в данных нет',
            hint:
              view === 'own'
                ? 'Долгов перед своими организациями на дату снимка нет.'
                : 'На дату снимка 1С не прислала ни одного долга перед поставщиками. Если они есть, вопрос к выгрузке взаиморасчётов.',
          }}
        >
          {() => <DebtTable rows={creditorRows.slice(0, 15)} total={num(creditors.data?.totals.amount)} />}
        </Panel>
      )}

      <DataNotes states={[debtors]} />
    </div>
  )
}

const SOURCE_ORDER = ['По документу', 'Выведено по контрагенту', 'Не определён']
const SOURCE_COLORS: Record<string, string> = {
  'По документу': 'var(--series-1)',
  'Выведено по контрагенту': 'var(--series-3)',
  'Не определён': 'var(--line-strong)',
}

/** Город у дебиторки: по документу-основанию, выведено по контрагенту, не определён. */
function DebtBranches({ state }: { state: ReturnType<typeof useDebtBranches> }) {
  const rows = state.data?.rows ?? []
  const total = rows.reduce((sum, row) => sum + num(row.amount), 0)
  const bySource = SOURCE_ORDER.map((source) => ({
    source,
    amount: rows.filter((row) => row.branch_source === source).reduce((sum, row) => sum + num(row.amount), 0),
  }))
  const cities = new Map<string, { name: string; document: number; derived: number }>()
  for (const row of rows) {
    if (row.branch_source === 'Не определён') continue
    const key = rowKey(row, 'branch')
    const item = cities.get(key) ?? { name: String(row.branch ?? '—'), document: 0, derived: 0 }
    if (row.branch_source === 'По документу') item.document += num(row.amount)
    else item.derived += num(row.amount)
    cities.set(key, item)
  }
  const list = [...cities.values()].sort((a, b) => b.document + b.derived - (a.document + a.derived))
  const unknown = bySource[2]!.amount

  return (
    <Panel
      index={2}
      title="Дебиторка по городам"
      subtitle="Город — из документа-основания; где его нет, по последней продаже контрагенту («выведено»)"
      states={[state]}
      skeleton={<SkeletonRows rows={4} height={16} />}
      empty={{ when: total === 0, title: 'Дебиторской задолженности нет' }}
    >
      {() => (
        <div className="stack" style={{ gap: 14 }}>
          <SegmentBar
            ariaLabel="Откуда известен город у дебиторки"
            segments={bySource.map((item) => ({ key: item.source, label: item.source, color: SOURCE_COLORS[item.source]!, value: item.amount }))}
          />
          {/* Легенда полосы: без неё полоса одного цвета ничего не говорит. */}
          <div className="chart-legend chart-legend--tight">
            {bySource
              .filter((item) => item.amount > 0)
              .map((item) => (
                <span key={item.source} className="chart-legend__item">
                  <span className="chart-legend__swatch" style={{ background: SOURCE_COLORS[item.source] }} aria-hidden="true" />
                  {item.source === 'Выведено по контрагенту' ? 'Выведено' : item.source} — {money(item.amount)} ·{' '}
                  {formatPercent((item.amount / total) * 100)}
                </span>
              ))}
          </div>
          {unknown > 0 && (
            <Caveat tone="warn">
              У {formatPercent((unknown / total) * 100)} долга ({money(unknown)}) город не определить: основание —
              платёж без подразделения или документ, которого нет в выгрузке, а продаж и поступлений по контрагенту нет.
              Поэтому директорам филиалов долги по их городу пока не выдаются — они увидели бы заниженную сумму.
            </Caveat>
          )}
          <div className="table-wrap">
            <table className="fin-table">
              <thead>
                <tr>
                  <th>Город</th>
                  <th className="num">По документу</th>
                  <th className="num">Выведено</th>
                  <th className="num">Всего</th>
                  <th className="num">Доля</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item, index) => (
                  <tr key={item.name} className="cascade" style={{ '--i': Math.min(index, 7) } as CSSProperties}>
                    <td className="strong">{item.name}</td>
                    <td className="num mono">{formatMoneyShort(item.document)}</td>
                    <td className="num mono muted">{item.derived > 0 ? formatMoneyShort(item.derived) : '—'}</td>
                    <td className="num mono">{formatMoneyShort(item.document + item.derived)}</td>
                    <td className="num mono muted">{formatPercent(((item.document + item.derived) / total) * 100)}</td>
                  </tr>
                ))}
                {unknown > 0 && (
                  <tr>
                    <td className="muted">Город не определён</td>
                    <td className="num mono muted">—</td>
                    <td className="num mono muted">—</td>
                    <td className="num mono">{formatMoneyShort(unknown)}</td>
                    <td className="num mono muted">{formatPercent((unknown / total) * 100)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Panel>
  )
}

function DebtTable({
  rows,
  total,
  onOpen,
}: {
  rows: Record<string, unknown>[]
  total: number
  onOpen?: (row: Record<string, unknown>, element: HTMLElement) => void
}) {
  return (
    <div className="table-wrap">
      <table className={`fin-table${onOpen ? ' fin-table--interactive' : ''}`}>
        <thead>
          <tr>
            <th>Контрагент</th>
            <th className="num">Долг</th>
            <th className="num">Доля</th>
            <th className="num">Просрочено</th>
            <th className="num">Свыше 60 дн.</th>
            <th className="num">Макс. просрочка</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const key = rowKey(row, 'counterparty')
            const days = toNumber(row.max_overdue_days)
            return (
              <tr
                key={key}
                className="cascade"
                style={{ '--i': Math.min(index, 7) } as CSSProperties}
                tabIndex={onOpen ? 0 : undefined}
                onClick={onOpen ? (event) => onOpen(row, event.currentTarget) : undefined}
                onKeyDown={
                  onOpen
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          onOpen(row, event.currentTarget)
                        }
                      }
                    : undefined
                }
              >
                <td className="strong">{String(row.counterparty ?? '—')}</td>
                <td className="num mono">{formatMoneyShort(row.amount)}</td>
                <td className="num mono muted">{total > 0 ? formatPercent((num(row.amount) / total) * 100) : '—'}</td>
                <td className={`num mono${overdueOf(row) > 0 ? ' cell-negative' : ' muted'}`}>{formatMoneyShort(overdueOf(row))}</td>
                <td className={`num mono${over60(row) > 0 ? ' cell-negative' : ' muted'}`}>{formatMoneyShort(over60(row))}</td>
                <td className="num mono">{days ? `${formatNumber(days)} дн.` : '—'}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
